import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import { toEdgeShadow } from "@/app/shadow.utils";

const SKELETON_ROW_COUNT = 15;

interface ListGridProps {
  header: ReactNode;
  children: ReactNode;
  footer: ReactNode;
  /** Shown above the grid, e.g. an error alert. */
  notice?: ReactNode;
  /** First load: shows `skeletonRow` repeated instead of rows. */
  isLoading: boolean;
  skeletonRow: ReactNode;
  /** A refetch: the current rows stay and a thin bar under the headings shows progress. */
  isFetching: boolean;
  /** Shown instead of rows when there are none; null when there are rows. */
  emptyText: string | null;
}

/**
 * A paged list page's frame (order and inventory lists): headings pinned at the top, rows below and
 * a ListGridFooter pinned at the bottom, sharing one scroll area. Lay the headings and every row
 * out on the same ListGridTemplate so the columns line up.
 */
export const ListGrid = (props: ListGridProps) => {
  const { header, children, footer, notice, isLoading, skeletonRow, isFetching, emptyText } = props;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      {notice}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
        }}
      >
        <Box sx={{ minWidth: "min-content" }}>
          <Box
            sx={{
              bgcolor: "background.default",
              boxShadow: (theme) => toEdgeShadow(theme, "bottom"),
              position: "sticky",
              pt: 1.5,
              top: 0,
              zIndex: 1,
            }}
          >
            {header}
            <Box sx={{ height: 2 }}>
              {isFetching && <LinearProgress sx={{ height: 2 }} />}
            </Box>
          </Box>
          {isLoading && Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
            <Box key={index}>
              {skeletonRow}
            </Box>
          ))}
          {emptyText && (
            <Typography
              color="text.secondary"
              sx={{ p: 3 }}
              variant="body2"
            >
              {emptyText}
            </Typography>
          )}
          {children}
        </Box>
      </Box>
      {footer}
    </Box>
  );
};
